import { Link } from "react-router-dom";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { COMPANY, SERVICES, PROCESS_STEPS } from "@/lib/constants";
import Reveal, { SectionHead } from "@/components/Reveal";

const Uslugi = () => (
  <>
    <SEO
      title="Usługi brukarskie – kostka brukowa, płyty tarasowe, palisady | AD Brikman Łęczna"
      description="Układanie kostki brukowej, płyt tarasowych i ceramicznych, płyt chodnikowych, palisad i obrzeży. Podjazdy, parkingi, opaski, tarasy, nawierzchnie pod garaże. Brukarstwo artystyczne. Łęczna, Lublin, Świdnik i okolice."
      path="/uslugi"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: SERVICES.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: { "@type": "Service", name: s.title, description: s.desc, provider: { "@type": "LocalBusiness", name: COMPANY.fullName, telephone: "+48535616324" }, areaServed: "woj. lubelskie" },
        })),
      }}
    />

    <section className="grain relative overflow-hidden bg-ink pt-36 pb-16 lg:pt-44">
      <div className="paver-bg absolute inset-0 opacity-60" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-5">Usługi</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display max-w-4xl text-5xl font-black uppercase leading-[0.95] tracking-tight text-pearl sm:text-7xl" data-testid="uslugi-title">
            Wszystko, co tworzy <span className="text-amber">nawierzchnie</span>
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stonemute sm:text-lg">
            Układamy kostkę brukową, płyty tarasowe, płyty ceramiczne, płyty chodnikowe, palisady i obrzeża — na obiektach prywatnych oraz dla firm i administratorów nieruchomości.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8" data-testid="uslugi-list">
      {SERVICES.map((s, i) => (
        <Reveal key={s.id} delay={0.05}>
          <article className={`mb-6 grid gap-8 border-b border-white/5 pb-14 lg:grid-cols-2 lg:items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`} data-testid={`usluga-${s.id}`}>
            <div className={`notch overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <img src={s.photo} alt={`${s.title} — AD Brikman`} loading="lazy" className="h-72 w-full object-cover lg:h-96" />
            </div>
            <div>
              <p className="eyebrow mb-3">{String(i + 1).padStart(2, "0")} · {s.short}</p>
              <h2 className="font-display text-3xl font-extrabold uppercase text-pearl sm:text-4xl">{s.title}</h2>
              <p className="mt-4 max-w-xl leading-relaxed text-stonemute">{s.desc}</p>
            </div>
          </article>
        </Reveal>
      ))}
    </section>

    <section className="border-y border-white/10 bg-graph grain" data-testid="uslugi-process">
      <div className="paver-bg">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
          <SectionHead eyebrow="Etapy realizacji" title="Jak pracujemy — od A do Z" sub="Możemy poprowadzić całą inwestycję albo wykonać wybrany zakres. Zawsze z pomocą w doborze i zakupie materiałów." />
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="card-edge h-full p-6" data-testid={`uslugi-step-${s.n}`}>
                  <span className="font-mono text-sm text-amber">{s.n}</span>
                  <h3 className="font-display mt-3 text-xl font-bold uppercase leading-snug text-pearl">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stonemute">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-24 text-center sm:px-8" data-testid="uslugi-cta">
      <Reveal>
        <h2 className="font-display text-4xl font-black uppercase text-pearl sm:text-5xl">
          Nie wiesz, od czego zacząć? <span className="text-amber">Doradzimy.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <Link to="/kontakt" data-testid="uslugi-cta-wycena" className="btn-amber px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em]">
            Bezpłatna wycena
          </Link>
          <a href={`tel:${COMPANY.phoneRaw}`} data-testid="uslugi-cta-call" className="btn-ghost px-8 py-4 font-mono text-xs uppercase tracking-[0.2em]">
            {COMPANY.phone}
          </a>
        </div>
      </Reveal>
    </section>
  </>
);

export default Uslugi;
